import { useMemo, useRef, type ReactNode } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, MeshDistortMaterial, Sparkles } from "@react-three/drei";
import * as THREE from "three";
import { pointer } from "../../lib/pointer";

/** Smoothly eases the shared pointer and applies parallax to the whole group. */
function ParallaxRig({ children }: { children: ReactNode }) {
  const group = useRef<THREE.Group>(null);

  useFrame((_, delta) => {
    const t = Math.min(1, delta * 4);
    pointer.x = THREE.MathUtils.lerp(pointer.x, pointer.targetX, t);
    pointer.y = THREE.MathUtils.lerp(pointer.y, pointer.targetY, t);

    if (group.current) {
      group.current.rotation.y = THREE.MathUtils.lerp(
        group.current.rotation.y,
        pointer.x * 0.6,
        0.06,
      );
      group.current.rotation.x = THREE.MathUtils.lerp(
        group.current.rotation.x,
        pointer.y * 0.35,
        0.06,
      );
    }
  });

  return <group ref={group}>{children}</group>;
}

/** A glowing, slowly morphing icosahedron wrapped in a wireframe shell. */
function CoreShape() {
  const inner = useRef<THREE.Mesh>(null);
  const wire = useRef<THREE.Mesh>(null);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    if (inner.current) {
      inner.current.rotation.y += delta * 0.25;
      inner.current.rotation.z += delta * 0.1;
    }
    if (wire.current) {
      wire.current.rotation.y -= delta * 0.12;
      wire.current.rotation.x += delta * 0.06;
      wire.current.scale.setScalar(1.18 + Math.sin(t * 0.8) * 0.04);
    }
  });

  return (
    <group>
      <mesh ref={inner}>
        <icosahedronGeometry args={[1.55, 6]} />
        <MeshDistortMaterial
          color="#1b1035"
          emissive="#8b5cf6"
          emissiveIntensity={0.55}
          roughness={0.15}
          metalness={0.85}
          distort={0.42}
          speed={1.8}
        />
      </mesh>

      <mesh ref={wire}>
        <icosahedronGeometry args={[1.55, 1]} />
        <meshBasicMaterial
          color="#8b5cf6"
          wireframe
          transparent
          opacity={0.14}
        />
      </mesh>
    </group>
  );
}

/** A tilted neon torus ring rotating around the core. */
function OrbitRing() {
  const ring = useRef<THREE.Mesh>(null);
  const orbit = useRef<THREE.Group>(null);

  useFrame((_, delta) => {
    if (ring.current) ring.current.rotation.z += delta * 0.6;
    if (orbit.current) orbit.current.rotation.y += delta * 0.18;
  });

  return (
    <group ref={orbit} rotation={[Math.PI / 2.2, 0, 0]}>
      <mesh ref={ring}>
        <torusGeometry args={[2.7, 0.012, 16, 128]} />
        <meshBasicMaterial color="#22d3ee" transparent opacity={0.5} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[2.35, 0.008, 16, 128]} />
        <meshBasicMaterial color="#d946ef" transparent opacity={0.35} />
      </mesh>
    </group>
  );
}

/** A handful of tiny moons orbiting in a gentle elliptical path. */
function Moons() {
  const group = useRef<THREE.Group>(null);
  const moons = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => ({
        radius: 3.1 + (i % 3) * 0.45,
        speed: 0.25 + (i % 4) * 0.12,
        size: 0.05 + (i % 3) * 0.03,
        offset: (i / 7) * Math.PI * 2,
        color: i % 2 === 0 ? "#a78bfa" : "#22d3ee",
      })),
    [],
  );

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    group.current?.children.forEach((moon, i) => {
      const m = moons[i];
      if (!m) return;
      const a = t * m.speed + m.offset;
      moon.position.set(Math.cos(a) * m.radius, Math.sin(a * 1.4) * 0.9, Math.sin(a) * m.radius * 0.6);
    });
  });

  return (
    <group ref={group}>
      {moons.map((m, i) => (
        <mesh key={i}>
          <sphereGeometry args={[m.size, 16, 16]} />
          <meshBasicMaterial color={m.color} />
        </mesh>
      ))}
    </group>
  );
}

function SceneContents() {
  return (
    <>
      <ambientLight intensity={0.55} />
      <directionalLight position={[5, 6, 4]} intensity={1.6} color="#ffffff" />
      <pointLight position={[4, 3, 4]} intensity={42} color="#8b5cf6" />
      <pointLight position={[-4, -2, -3]} intensity={34} color="#22d3ee" />
      <pointLight position={[0, -3, 2]} intensity={20} color="#d946ef" />

      <ParallaxRig>
        <Float speed={1.4} rotationIntensity={0.4} floatIntensity={1.1}>
          <CoreShape />
        </Float>
        <OrbitRing />
        <Moons />
      </ParallaxRig>

      <Sparkles
        count={110}
        scale={[11, 7, 6]}
        size={2.2}
        speed={0.35}
        opacity={0.55}
        color="#c4b5fd"
      />
      <Sparkles
        count={50}
        scale={[7, 5, 4]}
        size={4}
        speed={0.22}
        opacity={0.4}
        color="#67e8f9"
      />
    </>
  );
}

export function HeroCanvas() {
  return (
    <Canvas
      dpr={[1, 1.75]}
      camera={{ position: [0, 0, 7.2], fov: 42 }}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      style={{ background: "transparent" }}
    >
      <fog attach="fog" args={["#05060b", 6, 16]} />
      <SceneContents />
    </Canvas>
  );
}
